import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  HardHat, ArrowLeft, ArrowRight, MagnifyingGlass,
  CheckCircle, XCircle, SpinnerGap, Storefront, ShieldCheck, RocketLaunch, WhatsappLogo,
} from "@phosphor-icons/react";
import { ChevronDown } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api, { verifyGst } from "@/lib/api";
import { cn } from "@/lib/utils";
import CityGoogleAutocomplete from "@/components/CityGoogleAutocomplete";

const FEATURES = [
  { icon: Storefront, text: "Get RFQs from verified buyers in your city" },
  { icon: ShieldCheck, text: "GST-verified onboarding — trusted network" },
  { icon: RocketLaunch, text: "Start receiving leads within 24 hours" },
];

const SUPPORT_WHATSAPP_NUMBER = "918920893912";

function buildSupplierPasswordResetMessage(email) {
  return `Hi Scaffo India ! please share new password for ${email}`;
}

function supplierPasswordResetWhatsAppUrl(email) {
  const text = encodeURIComponent(buildSupplierPasswordResetMessage(email));
  return `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${text}`;
}

function supplierPostAuthPath(search) {
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  params.delete("t");
  params.delete("token");
  const q = params.toString();
  return q ? `/supplier?${q}` : "/supplier";
}

function magicTokenFromSearch(search) {
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  return (params.get("t") || params.get("token") || "").trim();
}

function FieldLabel({ children, active }) {
  return (
    <label
      className="block text-[10px] font-bold tracking-[0.15em] uppercase mb-2 transition-colors duration-150"
      style={{ color: active ? "#FF7A00" : "#aaa" }}
    >
      {children}
    </label>
  );
}

function UnderlineInput({ label, id, type = "text", placeholder, value, onChange, onKeyDown, testId, autoComplete, disabled, extraClass = "" }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <FieldLabel active={focused}>{label}</FieldLabel>
      <input
        id={id}
        data-testid={testId}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        autoComplete={autoComplete}
        disabled={disabled}
        className={`w-full bg-transparent text-neutral-900 text-sm py-2.5 outline-none placeholder-neutral-400 disabled:opacity-40 ${extraClass}`}
        style={{ borderBottom: `2px solid ${focused ? "#FF7A00" : "#D8D2C8"}`, transition: "border-color 0.2s" }}
      />
    </div>
  );
}

function SectionDivider({ label }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="flex-1 h-px" style={{ background: "#E8E3D9" }} />
      <span className="text-[10px] font-bold tracking-[0.15em] uppercase" style={{ color: "#C4B9A8" }}>{label}</span>
      <div className="flex-1 h-px" style={{ background: "#E8E3D9" }} />
    </div>
  );
}

export default function SupplierLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithMagicToken, register, logout, user } = useAuth();
  const [tab, setTab] = useState("login");
  const [loginMode, setLoginMode] = useState("signin");
  const [loading, setLoading] = useState(false);
  const [magicBusy, setMagicBusy] = useState(false);
  const magicToken = useMemo(() => magicTokenFromSearch(location.search), [location.search]);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");

  const [gstNumber, setGstNumber] = useState("");
  const [gstVerified, setGstVerified] = useState(false);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstError, setGstError] = useState("");

  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regCompany, setRegCompany] = useState("");
  const [regType, setRegType] = useState("hybrid");
  const [regCities, setRegCities] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  const [regSelectedProductIds, setRegSelectedProductIds] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [productPopoverOpen, setProductPopoverOpen] = useState(false);

  const normalizeCity = (v) => String(v || "").trim().replace(/\s+/g, " ");

  useEffect(() => {
    api.get("/products?limit=200").then((r) => {
      const items = r.data.items || r.data || [];
      setCatalogProducts(Array.isArray(items) ? items : []);
    }).catch(() => setCatalogProducts([]));
  }, []);

  useEffect(() => {
    if (magicToken) return;
    if (user?.role === "supplier") navigate(supplierPostAuthPath(location.search), { replace: true });
  }, [user, location.search, navigate, magicToken]);

  const magicAttempted = useRef("");
  useEffect(() => {
    if (!magicToken || magicAttempted.current === magicToken) return;
    magicAttempted.current = magicToken;
    let cancelled = false;
    setMagicBusy(true);
    (async () => {
      try {
        const u = await loginWithMagicToken(magicToken);
        if (cancelled) return;
        if (u.role !== "supplier") {
          logout();
          toast.error("This login link is not for a supplier account.");
          return;
        }
        toast.success("Welcome back!");
        navigate(supplierPostAuthPath(location.search), { replace: true });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.response?.data?.detail || "This login link is invalid or expired. Please sign in.");
      } finally {
        if (!cancelled) setMagicBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // Intentionally omit loginWithMagicToken: AuthProvider recreates it each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [magicToken, location.search, navigate]);

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) { toast.error("Fill all fields"); return; }
    setLoading(true);
    try {
      const u = await login(loginEmail, loginPassword);
      if (u.role !== "supplier") {
        logout();
        toast.error(u.role === "buyer" ? "Buyer accounts use the buyer sign-in page." : "This portal is for suppliers only.");
        setLoading(false); return;
      }
      toast.success("Welcome back!");
      navigate(supplierPostAuthPath(location.search));
    } catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    setLoading(false);
  };

  const handleForgotPasswordRequest = () => {
    const email = forgotEmail.trim().toLowerCase();
    if (!email) {
      toast.error("Enter your registered email");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    window.open(supplierPasswordResetWhatsAppUrl(email), "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp to request a new password");
  };

  const handleVerifyGst = async () => {
    const trimmed = gstNumber.trim().toUpperCase();
    if (!trimmed) { toast.error("Enter GST number"); return; }
    setGstLoading(true); setGstError("");
    try {
      const data = await verifyGst(trimmed);
      setGstVerified(true);
      if (data.trade_nam) setRegCompany(data.trade_nam);
      if (data.lgnm) setRegName(data.lgnm);
      toast.success("GST verified! Details auto-filled.");
    } catch (err) {
      setGstVerified(false);
      const msg = err.response?.data?.detail || err.message || "GST verification failed";
      setGstError(msg); toast.error(msg);
    } finally { setGstLoading(false); }
  };

  const handleRegister = async () => {
    if (!regName || !regEmail || !regPassword || !regPhone) { toast.error("Fill all required fields"); return; }
    setLoading(true);
    try {
      const trimmedGst = gstNumber.trim().toUpperCase();
      await register({
        name: regName, email: regEmail, password: regPassword, phone: regPhone,
        company_name: regCompany, supplier_type: regType, cities_served: regCities,
        gst_number: trimmedGst || undefined,
        products_supplied: regSelectedProductIds.map((product_id) => ({ product_id, quantity: 1 })),
      });
      toast.success("Registration successful!");
      navigate(supplierPostAuthPath(location.search));
    } catch (err) { toast.error(err.response?.data?.detail || "Registration failed"); }
    setLoading(false);
  };

  const toggleCity = (city) =>
    setRegCities((p) => (p.includes(city) ? p.filter((c) => c !== city) : [...p, city]));

  const addRegCityFromSearch = useCallback((selected) => {
    const normalized = normalizeCity(selected);
    if (!normalized) return;
    setRegCities((prev) => {
      if (prev.some((city) => city.toLowerCase() === normalized.toLowerCase())) return prev;
      return [...prev, normalized];
    });
    setCitySearch("");
  }, []);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    return q ? catalogProducts.filter((p) => p.name.toLowerCase().includes(q)) : catalogProducts;
  }, [productSearch, catalogProducts]);

  const toggleCatalogProduct = (id) => setRegSelectedProductIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const clearAllCatalogProducts = () => setRegSelectedProductIds([]);

  const resetForm = () => {
    setGstNumber(""); setGstVerified(false); setGstError("");
    setRegName(""); setRegEmail(""); setRegPassword(""); setRegPhone(""); setRegCompany("");
    setRegType("hybrid"); setRegCities([]);
    setCitySearch("");
    setRegSelectedProductIds([]); setProductSearch(""); setProductPopoverOpen(false);
  };

  /* ─── shared trigger style ─── */
  const triggerCls = "flex h-10 w-full items-center justify-between rounded-xl border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF7A00] focus:ring-offset-0 transition-colors";
  const triggerStyle = { borderColor: "#D8D2C8" };

  return (
    <div className="min-h-screen flex" style={{ background: "#F7F4EF" }}>

      {/* ── LEFT BRANDING PANEL ───────────────────────────── */}
      <div
        className="hidden lg:flex flex-col justify-between w-[40%] shrink-0 relative overflow-hidden p-12 xl:p-16"
        style={{ background: "linear-gradient(145deg, #1a1a1a 0%, #2d1a08 60%, #1a1a1a 100%)" }}
      >
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: "linear-gradient(#FF7A00 1px,transparent 1px),linear-gradient(90deg,#FF7A00 1px,transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="absolute top-0 left-0 bottom-0 w-1 bg-[#FF7A00]" />

        <div className="relative flex items-center gap-3">
          <HardHat size={28} weight="bold" style={{ color: "#FF7A00" }} />
          <span className="text-white font-black tracking-tight text-lg">SCAFFOINDIA</span>
        </div>

        <div className="relative">
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-5" style={{ color: "#FF7A00" }}>
            Supplier portal
          </p>
          <h2
            className="font-black text-white leading-none tracking-tighter mb-6"
            style={{ fontSize: "clamp(2rem, 3.2vw, 3.2rem)" }}
          >
            Grow your<br />
            supply business.<br />
            <span style={{ color: "#FF7A00" }}>Join India's network.</span>
          </h2>
          <p className="text-sm leading-relaxed mb-10" style={{ color: "#888", maxWidth: "320px" }}>
            Register your scaffolding business, list your products and service cities, and start receiving verified buyer RFQs.
          </p>
          <div className="space-y-4">
            {FEATURES.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(255,122,0,0.15)" }}>
                  <Icon size={16} weight="bold" style={{ color: "#FF7A00" }} />
                </div>
                <p className="text-sm font-medium" style={{ color: "#bbb" }}>{text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <p className="text-xs" style={{ color: "#555" }}>
            Buyer?{" "}
            <Link to="/account/login" className="font-bold underline underline-offset-2" style={{ color: "#FF7A00" }}>
              Use the buyer portal
            </Link>
          </p>
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ─────────────────────────────── */}
      <div className="flex-1 flex flex-col min-h-screen overflow-y-auto" style={{ background: "#F7F4EF" }}>

        {/* Mobile top bar */}
        <div className="flex items-center justify-between px-6 py-5 lg:hidden border-b" style={{ borderColor: "#E8E3D9" }}>
          <div className="flex items-center gap-2">
            <HardHat size={22} weight="bold" style={{ color: "#FF7A00" }} />
            <span className="font-black tracking-tight text-neutral-900">SCAFFOINDIA</span>
          </div>
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="supplier-back-to-home-btn"
            className="flex items-center gap-1.5 text-xs font-semibold hover:opacity-80"
            style={{ color: "#999" }}
          >
            <ArrowLeft size={14} weight="bold" /> Home
          </button>
        </div>

        <div className="flex-1 flex items-start justify-center px-6 py-12 sm:px-10 lg:px-14 xl:px-20">
          <div className="w-full max-w-lg">

            {/* Desktop back */}
            <button
              type="button"
              onClick={() => navigate("/")}
              data-testid="supplier-back-to-home-btn"
              className="hidden lg:flex items-center gap-1.5 text-xs font-semibold mb-10 hover:opacity-80"
              style={{ color: "#aaa" }}
            >
              <ArrowLeft size={13} weight="bold" /> Back to home
            </button>

            {!magicBusy && (
            <div className="flex items-center gap-1 mb-8 p-1 rounded-full" style={{ background: "#EDE8E0", border: "1px solid #DDD8CE" }}>
              {[["login", "Sign in"], ["register", "Register"]].map(([t, label]) => (
                <button
                  key={t}
                  type="button"
                  data-testid={t === "login" ? "login-tab" : "register-tab"}
                  onClick={() => { setTab(t); setLoginMode("signin"); if (t === "register") resetForm(); }}
                  className="flex-1 py-2.5 rounded-full text-sm font-bold transition-all duration-200"
                  style={{ background: tab === t ? "#FF7A00" : "transparent", color: tab === t ? "#fff" : "#999" }}
                >
                  {label}
                </button>
              ))}
            </div>
            )}

            {/* Heading */}
            <div className="mb-8">
              <h1
                className="font-black text-neutral-900 leading-tight tracking-tighter"
                style={{ fontSize: "clamp(1.5rem, 2.8vw, 2rem)" }}
              >
                {magicBusy
                  ? "Signing you in."
                  : tab === "login"
                    ? (loginMode === "forgot" ? "Reset your password." : "Welcome back.")
                    : "Register your business."}
              </h1>
              <p className="mt-2 text-sm" style={{ color: "#999" }}>
                {magicBusy
                  ? "Opening your supplier portal from the WhatsApp link…"
                  : tab === "login"
                    ? (loginMode === "forgot"
                      ? "Enter your email and we will open WhatsApp so you can request a new password from our team."
                      : "Sign in to your supplier account")
                    : "Enter your business details to get started"}
              </p>
            </div>

            {magicBusy && (
              <div className="flex items-center gap-2 text-sm" style={{ color: "#FF7A00" }} data-testid="supplier-magic-login-status">
                <SpinnerGap size={18} className="animate-spin" weight="bold" />
                Please wait
              </div>
            )}

            {/* ── LOGIN ───────────────────────────────────── */}
            {!magicBusy && tab === "login" && loginMode === "signin" && (
              <div className="space-y-6">
                <UnderlineInput id="s-email" label="Email" type="email" placeholder="your@email.com"
                  value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                  testId="supplier-login-email-input" autoComplete="email" />
                <div>
                  <UnderlineInput id="s-password" label="Password" type="password" placeholder="••••••••"
                    value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                    testId="supplier-login-password-input" autoComplete="current-password" />
                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      data-testid="supplier-forgot-password-link"
                      onClick={() => {
                        setForgotEmail(loginEmail.trim());
                        setLoginMode("forgot");
                      }}
                      className="text-xs font-semibold hover:opacity-80 transition-opacity"
                      style={{ color: "#FF7A00" }}
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>
                <button
                  data-testid="supplier-login-submit-btn"
                  type="button"
                  onClick={handleLogin}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-black transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                  style={{ background: "#FF7A00", color: "#fff" }}
                >
                  {loading ? "Signing in…" : <>Sign in <ArrowRight size={15} weight="bold" /></>}
                </button>
              </div>
            )}

            {!magicBusy && tab === "login" && loginMode === "forgot" && (
              <div className="space-y-6">
                <UnderlineInput
                  id="s-forgot-email"
                  label="Registered email *"
                  type="email"
                  placeholder="your@email.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleForgotPasswordRequest()}
                  testId="supplier-forgot-email-input"
                  autoComplete="email"
                />
                <p className="text-xs leading-relaxed" style={{ color: "#aaa" }}>
                  We will open WhatsApp with a pre-filled message to our support team. Send it from your number to request a new password.
                </p>
                <button
                  data-testid="supplier-forgot-password-submit-btn"
                  type="button"
                  onClick={handleForgotPasswordRequest}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-black transition-all hover:opacity-90 mt-2"
                  style={{ background: "#25D366", color: "#fff" }}
                >
                  <WhatsappLogo size={18} weight="fill" />
                  Request password on WhatsApp
                </button>
                <button
                  type="button"
                  data-testid="supplier-forgot-password-back-btn"
                  onClick={() => setLoginMode("signin")}
                  className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold hover:opacity-80 transition-opacity"
                  style={{ color: "#999" }}
                >
                  <ArrowLeft size={13} weight="bold" /> Back to sign in
                </button>
              </div>
            )}

            {/* ── REGISTER ────────────────────────────────── */}
            {!magicBusy && tab === "register" && (
              <div className="space-y-5">

                {/* GST (optional — verify to auto-fill, or enter details manually below) */}
                <div>
                  <FieldLabel active={false}>GST Number (optional)</FieldLabel>
                  <p className="text-xs mb-2" style={{ color: "#aaa" }}>
                    Verify to auto-fill company details, or skip and enter your information manually.
                  </p>
                  <div className="flex gap-2">
                    <input
                      data-testid="reg-gst-input"
                      placeholder="27AAACR5055K1Z7"
                      value={gstNumber}
                      onChange={(e) => { setGstNumber(e.target.value); setGstVerified(false); setGstError(""); }}
                      className={`flex-1 bg-white text-neutral-900 text-sm font-mono uppercase px-3 py-2.5 rounded-xl border outline-none transition-colors focus:ring-2 focus:ring-[#FF7A00] focus:ring-offset-0 ${
                        gstVerified ? "border-green-400 bg-green-50" : gstError ? "border-red-400 bg-red-50" : "border-[#D8D2C8]"
                      }`}
                    />
                    {gstVerified ? (
                      <button type="button" disabled className="flex items-center justify-center w-11 rounded-xl bg-green-500 text-white shrink-0">
                        <CheckCircle size={18} weight="fill" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleVerifyGst}
                        disabled={gstLoading || !gstNumber.trim()}
                        className="flex items-center justify-center w-11 rounded-xl border transition-colors hover:bg-orange-50 disabled:opacity-40 shrink-0"
                        style={{ borderColor: "#FF7A00", color: "#FF7A00" }}
                        title="Verify GST and auto-fill details"
                      >
                        {gstLoading ? <SpinnerGap size={18} className="animate-spin" /> : <MagnifyingGlass size={18} />}
                      </button>
                    )}
                  </div>
                  {gstVerified && (
                    <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                      <CheckCircle size={13} weight="fill" /> Verified — details auto-filled below
                    </p>
                  )}
                  {gstError && (
                    <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                      <XCircle size={13} weight="fill" /> {gstError}
                    </p>
                  )}
                </div>

                <div className="space-y-5">

                  <SectionDivider label="Your details" />

                  <div className="grid grid-cols-2 gap-5">
                    <UnderlineInput id="r-name" label="Full Name *" placeholder="Your name"
                      value={regName} onChange={(e) => setRegName(e.target.value)}
                      testId="reg-name-input" autoComplete="name" />
                    <UnderlineInput id="r-company" label="Company Name" placeholder="Company"
                      value={regCompany} onChange={(e) => setRegCompany(e.target.value)}
                      testId="reg-company-input" autoComplete="organization" />
                  </div>
                  <div className="grid grid-cols-2 gap-5">
                    <UnderlineInput id="r-email" label="Email *" type="email" placeholder="your@email.com"
                      value={regEmail} onChange={(e) => setRegEmail(e.target.value)}
                      testId="reg-email-input" autoComplete="email" />
                    <UnderlineInput id="r-phone" label="Phone *" placeholder="10-digit number"
                      value={regPhone} onChange={(e) => setRegPhone(e.target.value)}
                      testId="reg-phone-input" autoComplete="tel" />
                  </div>
                  <UnderlineInput id="r-password" label="Password *" type="password" placeholder="Create a password"
                    value={regPassword} onChange={(e) => setRegPassword(e.target.value)}
                    testId="reg-password-input" autoComplete="new-password" />

                  <SectionDivider label="Business info" />

                  {/* Supplier type */}
                  <div>
                    <FieldLabel active={false}>Supplier Type</FieldLabel>
                    <Select value={regType} onValueChange={setRegType}>
                      <SelectTrigger data-testid="reg-type-select" className={triggerCls} style={triggerStyle}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rental">Rental</SelectItem>
                        <SelectItem value="sale">Sale</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Cities served */}
                  <div className="relative z-20">
                    <div className="flex items-center justify-between mb-2">
                      <FieldLabel active={false}>Cities served</FieldLabel>
                      {regCities.length > 0 ? (
                        <button
                          type="button"
                          data-testid="reg-cities-clear-all"
                          onClick={() => setRegCities([])}
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full border transition-colors"
                          style={{ borderColor: "#D8D2C8", color: "#888" }}
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                    <p className="text-xs mb-2" style={{ color: "#aaa" }}>
                      Search with Google Places and add every city where you supply materials.
                    </p>
                    <CityGoogleAutocomplete
                      value={citySearch}
                      onChange={setCitySearch}
                      onSelect={addRegCityFromSearch}
                      placeholder="Search cities to add…"
                      predictionsPlacement="auto"
                      className="supplier-register-city-autocomplete"
                      containerTestId="reg-city-select"
                      inputTestId="reg-city-search"
                    />
                    {regCities.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                        {regCities.map((city) => (
                          <Badge
                            key={city}
                            data-testid={`city-badge-${city}`}
                            className="cursor-pointer rounded-full text-xs bg-orange-600 hover:bg-orange-700 text-white"
                            onClick={() => toggleCity(city)}
                          >
                            {city} ×
                          </Badge>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  {/* Products */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <FieldLabel active={false}>Products you supply</FieldLabel>
                      <div className="flex gap-2">
                        <button type="button" data-testid="reg-products-select-all"
                          onClick={() => setRegSelectedProductIds(catalogProducts.map((p) => p.id))}
                          disabled={catalogProducts.length === 0}
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full border transition-colors disabled:opacity-40"
                          style={{ borderColor: "#D8D2C8", color: "#888" }}>
                          All
                        </button>
                        <button type="button" data-testid="reg-products-clear-all"
                          onClick={clearAllCatalogProducts}
                          disabled={regSelectedProductIds.length === 0}
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full border transition-colors disabled:opacity-40"
                          style={{ borderColor: "#D8D2C8", color: "#888" }}>
                          Clear
                        </button>
                      </div>
                    </div>
                    <p className="text-xs mb-2" style={{ color: "#aaa" }}>Pick the catalog items you supply. Update quantities and rates later in your profile.</p>
                    <Popover open={productPopoverOpen} onOpenChange={(open) => { setProductPopoverOpen(open); if (!open) setProductSearch(""); }}>
                      <PopoverTrigger asChild>
                        <button type="button" role="combobox" aria-expanded={productPopoverOpen}
                          data-testid="reg-product-select"
                          className={cn(triggerCls, regSelectedProductIds.length === 0 && "text-neutral-400")}
                          style={triggerStyle}
                        >
                          <span className="line-clamp-1 text-left text-sm">
                            {regSelectedProductIds.length > 0 ? `${regSelectedProductIds.length} products selected` : "Search and select products"}
                          </span>
                          <ChevronDown className="h-4 w-4 shrink-0 opacity-40" aria-hidden />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" sideOffset={4} className="z-50 w-[min(calc(100vw-2rem),24rem)] p-0 flex flex-col overflow-hidden">
                        <div className="border-b border-neutral-200 p-2">
                          <Input value={productSearch} onChange={(e) => setProductSearch(e.target.value)}
                            placeholder="Search products..." autoComplete="off"
                            className="h-9 rounded-lg border-neutral-200" data-testid="reg-product-search" />
                        </div>
                        <div className="max-h-[min(260px,45vh)] overflow-y-auto p-1">
                          {catalogProducts.length === 0 ? (
                            <p className="px-2 py-2 text-xs text-neutral-500">Loading catalog…</p>
                          ) : filteredProducts.length === 0 ? (
                            <p className="px-2 py-2 text-xs text-neutral-500">No products found</p>
                          ) : filteredProducts.map((p) => {
                            const sel = regSelectedProductIds.includes(p.id);
                            return (
                              <button key={p.id} type="button" data-testid={`product-option-${p.id}`}
                                onClick={() => toggleCatalogProduct(p.id)}
                                className={cn("flex w-full items-center justify-between rounded-lg py-2 px-2 text-sm hover:bg-accent hover:text-accent-foreground", sel && "bg-accent/60")}>
                                <span className="truncate pr-2" title={p.name}>{p.name}</span>
                                {sel && <span className="text-xs font-bold shrink-0" style={{ color: "#FF7A00" }}>Selected</span>}
                              </button>
                            );
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                    {regSelectedProductIds.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
                        {catalogProducts.filter((p) => regSelectedProductIds.includes(p.id)).map((p) => (
                          <Badge key={p.id} data-testid={`reg-product-${p.id}`}
                            className="cursor-pointer rounded-full text-xs bg-orange-600 hover:bg-orange-700 text-white max-w-full truncate"
                            title={p.name} onClick={() => toggleCatalogProduct(p.id)}>
                            {p.name} ×
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit */}
                <button
                  data-testid="register-submit-btn"
                  type="button"
                  onClick={handleRegister}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-black transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                  style={{ background: "#FF7A00", color: "#fff" }}
                >
                  {loading ? "Creating account…" : <>Register <ArrowRight size={15} weight="bold" /></>}
                </button>
              </div>
            )}

            {/* Mobile buyer link */}
            <p className="mt-8 text-sm text-center lg:hidden" style={{ color: "#aaa" }}>
              Buyer?{" "}
              <Link to="/account/login" className="font-bold" style={{ color: "#FF7A00" }}>Buyer portal</Link>
            </p>

          </div>
        </div>
      </div>
    </div>
  );
}
